import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Eye, Gift, Sparkles, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FreeRegistration = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register-free?email=${encodeURIComponent(formData.email)}&username=${encodeURIComponent(formData.username)}&password=${encodeURIComponent(formData.password)}`);


      const { access_token, user } = response.data;
      
      // Store token and user
      localStorage.setItem('tkale_token', access_token);
      localStorage.setItem('tkale_user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success('🎉 Bienvenue sur T-Kalé ! 10 crédits offerts !');
      
      // Redirect to bubbles
      navigate('/');
      
    } catch (error) {
      console.error('Registration error:', error);
      nano -l +61 /home/ubuntu/apps/annex-studio/frontend/src/components/FreeRegistration.js
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'linear-gradient(to bottom, #001a33 0%, #003d66 50%, #001a33 100%)'
      }}
    >
      {/* Animated background particles */}
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={i}
          initial={{
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight
          }}
          animate={{
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{
            duration: 5 + Math.random() * 5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute w-2 h-2 bg-cyan-300 rounded-full"
        />
      ))}

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <Card className="bg-gradient-to-br from-teal-900/90 to-blue-900/90 border-teal-500 backdrop-blur-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Eye className="w-16 h-16 text-teal-400" />
            </div>
            <CardTitle className="text-3xl text-white">
              Découvrez T-Kalé
            </CardTitle>
            <CardDescription className="text-teal-200 text-lg">
              Inscription gratuite • Pas de carte bancaire
            </CardDescription>
          </CardHeader>

          <CardContent>
            {/* Benefits */}
            <div className="bg-teal-800/50 rounded-lg p-4 mb-6">
              <h3 className="text-white font-semibold mb-3 flex items-center">
                <Gift className="w-5 h-5 mr-2 text-yellow-400" />
                Ce que vous recevez :
              </h3>
              <ul className="space-y-2 text-teal-100">
                <li className="flex items-center">
                  <Sparkles className="w-4 h-4 mr-2 text-yellow-400" />
                  10 crédits T-Kalé offerts
                </li>
                <li className="flex items-center">
                  <Sparkles className="w-4 h-4 mr-2 text-yellow-400" />
                  Accès en lecture à toutes les bulles
                </li>
                <li className="flex items-center">
                  <Sparkles className="w-4 h-4 mr-2 text-yellow-400" />
                  Preview de tous les modules
                </li>
                <li className="flex items-center">
                  <Sparkles className="w-4 h-4 mr-2 text-yellow-400" />
                  Fonctionnalités basiques gratuites
                </li>
              </ul>
            </div>

            {/* Registration Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-white">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-teal-950/50 text-white border-teal-600"
                  placeholder="votre@email.com"
                  required
                />
              </div>

              <div>
                <Label htmlFor="username" className="text-white">Nom d'utilisateur</Label>
                <Input
                  id="username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  className="bg-teal-950/50 text-white border-teal-600"
                  placeholder="Votre nom"
                  required
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-white">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="bg-teal-950/50 text-white border-teal-600"
                  placeholder="••••••••"
                  required
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-white">Confirmer le mot de passe</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="bg-teal-950/50 text-white border-teal-600"
                  placeholder="••••••••"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-teal-600 hover:bg-teal-500 text-white font-semibold py-6 text-lg"
              >
                {loading ? 'Création en cours...' : '🚀 Commencer l\'aventure'}
              </Button>
            </form>

            {/* Back to login */}
            <div className="mt-6 text-center">
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="text-teal-300 hover:text-teal-200"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Déjà un compte ? Se connecter
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-cyan-300 text-sm mt-6"
        >
          En vous inscrivant, vous acceptez de plonger dans l'univers T-Kalé 🌊
        </motion.p>
      </motion.div>
    </div>
  );
};

export default FreeRegistration;
